/* eslint-disable max-lines */
import {
  DayGroupRange,
  DayGroupRangesConfig,
  MotionDetectionWindow,
} from '@hakimo-ui/hakimo/types';
import { getPreviousDayOfWeek } from '@hakimo-ui/hakimo/util';
import dayjs from 'dayjs';
import dayjsTimezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { DayLocationMonitoringConfig } from '../sop-workflow/util';
dayjs.extend(utc);
dayjs.extend(dayjsTimezone);

export const convertToSeconds = (time: string) => {
  const [hours, minutes, seconds] = time.split(':').map(Number);
  return hours * 60 * 60 + minutes * 60 + (seconds ?? 0);
};

/**
 * Returns monitoring configuration for a specific day relative to today
 * @param detectionWindow - Motion detection window configuration
 * @param daysOffset - Number of days offset from today (0 = today, 1 = tomorrow, etc.)
 * @returns Day location monitoring configuration
 */
export function getMonitoringConfigForDay(
  detectionWindow: MotionDetectionWindow,
  daysOffset = 0
): DayLocationMonitoringConfig {
  if (!detectionWindow) {
    return { timezone: 'UTC', dayOfWeek: '', ranges: undefined };
  }

  const locTimezone = detectionWindow.timezone;
  const nowInTz = dayjs().tz(locTimezone).add(daysOffset, 'day');
  const targetDayOfWeek = nowInTz.format('dddd').toUpperCase();
  const previousDayOfWeek = getPreviousDayOfWeek(targetDayOfWeek);

  // Get ranges for the target day
  const targetDayGroupRanges = findRangesForDay(
    detectionWindow.dayGroupRanges,
    targetDayOfWeek
  );

  // Get ranges for the previous day that might extend into the target day
  const previousDayGroupRanges = findRangesForDay(
    detectionWindow.dayGroupRanges,
    previousDayOfWeek
  );

  // Normalize ranges for the target day
  const normalizedRanges = normalizeRanges(
    targetDayGroupRanges,
    previousDayGroupRanges
  );

  return {
    timezone: locTimezone,
    dayOfWeek: targetDayOfWeek,
    ranges: normalizedRanges,
  };
}

/**
 * Returns monitoring configuration for today
 * @param detectionWindow - Motion detection window configuration
 * @returns Day location monitoring configuration for today
 */
export function getTodaysMonitoringConfig(
  detectionWindow: MotionDetectionWindow
): DayLocationMonitoringConfig {
  return getMonitoringConfigForDay(detectionWindow, 0);
}

/**
 * Returns monitoring configuration for tomorrow
 * @param detectionWindow - Motion detection window configuration
 * @returns Day location monitoring configuration for tomorrow
 */
export function getTomorrowsMonitoringConfig(
  detectionWindow: MotionDetectionWindow
): DayLocationMonitoringConfig {
  return getMonitoringConfigForDay(detectionWindow, 1);
}

/**
 * Finds ranges for a specific day of week from day group ranges
 * @param dayGroupRanges - All day group ranges
 * @param dayOfWeek - Target day of week
 * @returns Array of day group ranges
 */
function findRangesForDay(
  dayGroupRanges: DayGroupRangesConfig[],
  dayOfWeek: string
): DayGroupRange[] {
  return (
    dayGroupRanges.find((group) => group.daysOfWeekTz.includes(dayOfWeek))
      ?.ranges ?? []
  );
}

/**
 * Normalizes time ranges, handling cases where ranges cross midnight
 * @param targetDayRanges - Ranges for the target day
 * @param previousDayRanges - Ranges for the previous day
 * @returns Normalized ranges for the target day
 */
function normalizeRanges(
  targetDayRanges: DayGroupRange[],
  previousDayRanges: DayGroupRange[]
): DayGroupRange[] {
  // Create a copy to avoid mutating the original
  const normalizedRanges = [...targetDayRanges.map((range) => ({ ...range }))];

  // Fix any ranges where end time is before or equal to start time
  normalizedRanges.forEach((range) => {
    if (convertToSeconds(range.startAtTz) >= convertToSeconds(range.endAtTz)) {
      range.endAtTz = '23:59:59';
    }
  });

  // Add ranges from previous day that extend into the current day
  previousDayRanges.forEach((range) => {
    if (convertToSeconds(range.startAtTz) >= convertToSeconds(range.endAtTz)) {
      normalizedRanges.push({ startAtTz: '00:00:00', endAtTz: range.endAtTz });
    }
  });

  // Sort ranges by start time for better readability and logical order
  return normalizedRanges.sort(
    (a, b) => convertToSeconds(a.startAtTz) - convertToSeconds(b.startAtTz)
  );
}

// /**
//  * Format a “HH:mm:ss” time into 12‑hour clock with am/pm.
//  * E.g. "20:00:00" → "8pm", "10:30:00" → "10:30am"
//  */
// function format12Hour(time: string): string {
//   const [h, m, s] = time.split(':').map(Number);
//   const period = h >= 12 ? 'pm' : 'am';
//   let hour12 = h % 12;
//   if (hour12 === 0) hour12 = 12;
//   if (m === 0 && s === 0) {
//     return `${hour12}${period}`;
//   }
//   const minPadded = m < 10 ? `0${m}` : m;
//   return `${hour12}:${minPadded}${period}`;
// }

/**
 * Returns the currently active monitoring window, or a message if none.
 *
 * - If the window stays within the same day:
 *   “Monitoring Hours HH:mm:ss - HH:mm:ss”
 * - If the window spills past midnight:
 *   “Monitoring Hours 8pm (Today) - 10am (Tomorrow)”
 */
export function getCurrentMonitoringWindow(
  todayConfig: DayLocationMonitoringConfig,
  tomorrowConfig: DayLocationMonitoringConfig
): string {
  const { timezone: tz, ranges: todayRanges } = todayConfig;
  const now = dayjs().tz(tz);
  const nowSeconds = now.hour() * 3600 + now.minute() * 60 + now.second();

  if (!todayRanges?.length) {
    return 'No monitoring windows configured for today';
  }

  // find the range containing "now"
  const currentRange = todayRanges.find((r) => {
    const startSec = convertToSeconds(r.startAtTz);
    const endSec = convertToSeconds(r.endAtTz);
    return nowSeconds >= startSec && nowSeconds <= endSec;
  });

  if (!currentRange) {
    return `Current time is outside monitoring window`;
  }

  const endSec = convertToSeconds(currentRange.endAtTz);

  // detect cross‑midnight window: end at 23:59:59 plus tomorrow has 00:00:00 start
  if (
    endSec === 23 * 3600 + 59 * 60 + 59 &&
    tomorrowConfig.ranges?.[0]?.startAtTz === '00:00:00'
  ) {
    const start12 = currentRange.startAtTz;
    const end12 = tomorrowConfig.ranges[0].endAtTz;
    const todayName = now.format('ddd');
    const tomorrowName = now.add(1, 'day').format('ddd');
    return `Monitoring Hours: ${todayName} ${start12} - ${tomorrowName} ${end12}`;
  }

  const start12 = currentRange.startAtTz;
  const end12 = currentRange.endAtTz;
  const todayName = now.format('ddd');
  return `Monitoring Hours: ${todayName} ${start12} - ${todayName} ${end12}`;
}
